import { tsBulkActionsDelete, tsBulkActionsGet, tsBulkActionsPost } from '../../util/api'
import type {
    BigintId,
    LatestScheduleRuns,
    ScheduleDefinition,
    ScheduleDetails,
    SchedulePreview,
    SchedulePreviewRequest,
    ScheduleRunHistory,
    ScheduleTimezones,
} from './types'

const profileOptions = (profileId: BigintId) => ({ headers: { profileid: profileId } })

// The dashboard schedule route deliberately uses PUT. The shared ts-bulk client
// predates that verb, so keep this small adapter here rather than routing a
// state-changing request through Laravel's unrelated v5 proxy.
const tsBulkActionsPut = (
    endpoint: string,
    body: object,
    options: ReturnType<typeof profileOptions>,
) => {
    const metaTag = document.querySelector('meta[name="ts-bulk-actions-url"]')
    const baseUrl = metaTag?.getAttribute('content') || 'http://127.0.0.1:3000'
    return window.axios.put(`${baseUrl}/${endpoint}`, body, {
        ...options,
        withCredentials: true,
    })
}

export const scheduledSegmentsApi = {
    timezonesDraft: (profileId: BigintId) =>
        tsBulkActionsGet('recipes/v2/schedule/timezones', null, profileOptions(profileId)).then(
            (response) => response.data.data as ScheduleTimezones,
        ),
    timezones: (profileId: BigintId, recipeId: BigintId) =>
        tsBulkActionsGet(
            `recipes/v2/${recipeId}/schedule/timezones`,
            null,
            profileOptions(profileId),
        ).then((response) => response.data.data as ScheduleTimezones),
    get: (profileId: BigintId, recipeId: BigintId) =>
        tsBulkActionsGet(`recipes/v2/${recipeId}/schedule`, null, profileOptions(profileId)).then(
            (response) => response.data.data as ScheduleDetails | null,
        ),
    preview: (profileId: BigintId, recipeId: BigintId, draft: SchedulePreviewRequest) =>
        tsBulkActionsPost(
            `recipes/v2/${recipeId}/schedule/preview`,
            draft,
            profileOptions(profileId),
        ).then((response) => response.data.data as SchedulePreview),
    /**
     * Generic validation works before the recipe itself is manual. `recipe_id`
     * is only a peer-exclusion hint for an already-saved segment.
     */
    previewDraft: (
        profileId: BigintId,
        draft: SchedulePreviewRequest,
        recipeId?: BigintId | null,
    ) =>
        tsBulkActionsPost(
            'recipes/v2/schedule/preview',
            { ...draft, ...(recipeId ? { recipe_id: recipeId } : {}) },
            profileOptions(profileId),
        ).then((response) => response.data.data as SchedulePreview),
    save: (profileId: BigintId, recipeId: BigintId, draft: ScheduleDefinition) =>
        tsBulkActionsPut(`recipes/v2/${recipeId}/schedule`, draft, profileOptions(profileId)).then(
            (response) => response.data.data as ScheduleDetails,
        ),
    remove: (profileId: BigintId, recipeId: BigintId) =>
        tsBulkActionsDelete(`recipes/v2/${recipeId}/schedule`, null, profileOptions(profileId)),
    history: (
        profileId: BigintId,
        options: { limit?: number; cursor?: string; recipeId?: BigintId } = {},
    ) => {
        const { recipeId, ...page } = options
        return tsBulkActionsGet(
            'recipe-schedule-runs',
            { ...page, ...(recipeId ? { recipe_id: recipeId } : {}) },
            profileOptions(profileId),
        ).then((response) => response.data.data as ScheduleRunHistory)
    },
    latest: (profileId: BigintId) =>
        tsBulkActionsGet('recipe-schedule-runs/latest', null, profileOptions(profileId)).then(
            (response) => response.data.data as LatestScheduleRuns,
        ),
}

export const scheduledSegmentsApiError = (error: any): string => {
    const candidate =
        error?.response?.data?.message ?? error?.response?.data?.error ?? error?.message
    if (typeof candidate === 'string') return candidate
    if (candidate && typeof candidate === 'object') {
        if (typeof candidate.message === 'string') return candidate.message
        if (typeof candidate.code === 'string') return candidate.code
    }
    return 'Schedule request failed'
}
